/**
 * DataTab — the Action Center's KPI Data Entries surface (DEV-681 Phase 2).
 *
 * Renders one card per project that has actionable KPI data entries
 * (due + overdue), with up to three inline entry grids stacked inside:
 *   - Schedule (percent-complete)   → ScheduleDataEntry
 *   - Finance (actual spend)        → BudgetDataEntry
 *   - Mission (KPI value)           → MissionDataEntry
 *
 * Per Skip's locked design decisions:
 *   - Per-grid Save (not page-level Save All) — failure of one project's
 *     grid doesn't lose entries from another.
 *   - Default filter is "actionable only" — show just instances classified
 *     `due` or `overdue` against today. A toggle expands to all instances
 *     when the user wants full context for batch entry.
 *   - Read-only or no-instances projects don't render — keeps the surface
 *     focused on "what's waiting on me".
 *
 * Inputs come pre-attached to each project row by App.tsx:
 *   - data_reliability_expected_instances: the per-instance lateness inputs
 *   - action_center_schedule_instances / _finance_instances: SubKpiInstance[]
 *   - action_center_latest_schedule_points / _finance_points: keyed by instance_guid
 *   - action_center_can_edit_kpi: per-project permission gate
 *   - mission_subkpis + mission_targets: existing inputs reused as-is
 *
 * Source-of-truth: the page never re-derives lateness — it calls the
 * shared `classifyInstanceLateness` selector so the Data tab, the engine,
 * and the DataReliabilityTab matrix always agree on what's actionable.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  classifyInstanceLateness,
  type InstanceLatenessState,
} from '../../Data/DataReliability/perInstanceState';
import { excludeHse } from '../../Data/missionSubkpiFilter';
import type { ExpectedInstanceInput } from '../../kpi-engine/dataReliability';
import type { SubKpiInstance } from '../../Data/ProjectConfig/projectKpiConfigOverrides';
import ScheduleDataEntry from '../../Components/MetricEntryGrid/ScheduleDataEntry';
import BudgetDataEntry from '../../Components/MetricEntryGrid/BudgetDataEntry';
import MissionDataEntry from '../../Components/MetricEntryGrid/MissionDataEntry';
import styles from './DataTab.module.css';

// ── Constants ───────────────────────────────────────────────

/**
 * Default grace days when an instance config doesn't carry its own
 * `permissible_days_late`. Matches App.tsx's project-level fallback.
 */
const DEFAULT_GRACE_DAYS = 2;

/**
 * localStorage key for the collapsed-projects set. Stored as a JSON array
 * of upper-cased project GUIDs. Persisted across sessions so the user's
 * "I've handled this one" state doesn't reset on reload.
 */
const COLLAPSED_PROJECTS_KEY = 'l4e_action_center_data_collapsed_projects';

function loadCollapsedProjects(): Set<string> {
  try {
    const raw = window?.localStorage?.getItem(COLLAPSED_PROJECTS_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch {
    /* ignore corrupt JSON */
  }
  return new Set();
}

function saveCollapsedProjects(set: Set<string>): void {
  try {
    window?.localStorage?.setItem(
      COLLAPSED_PROJECTS_KEY,
      JSON.stringify([...set]),
    );
  } catch {
    /* localStorage may be unavailable (Safari private mode); ignore */
  }
}

// ── Helpers ─────────────────────────────────────────────────

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Build the per-row classification map: instance_id (UPPER) → state.
 * Pure function of the row's expected_instances + asOfDate; cheap enough
 * to recompute on every render without memo.
 */
function classifyAllForRow(
  expected: ExpectedInstanceInput[],
  asOfDate: string,
): Record<string, InstanceLatenessState> {
  const out: Record<string, InstanceLatenessState> = {};
  for (const inst of expected) {
    const state = classifyInstanceLateness({
      instance: inst,
      asOfDate,
      defaultGraceDays: DEFAULT_GRACE_DAYS,
    });
    out[String(inst.instance_id || '').toUpperCase()] = state;
  }
  return out;
}

/** Return true when the state should appear in the actionable-only view. */
function isActionableState(s: InstanceLatenessState | undefined): boolean {
  return s === 'due' || s === 'overdue';
}

/**
 * Filter a SubKpiInstance[] to just the actionable instances for a project.
 * Returns the input unchanged when `showAll` is true.
 */
function filterInstancesForView<
  T extends { instance_guid?: string }
>(
  instances: T[],
  classification: Record<string, InstanceLatenessState>,
  showAll: boolean,
): T[] {
  if (showAll) return instances;
  return instances.filter((inst) => {
    const id = String(inst?.instance_guid || '').toUpperCase();
    return isActionableState(classification[id]);
  });
}

/**
 * Filter mission sub-KPIs to actionable. Mission instances key on
 * `outcome_id || instance_guid || display_name` (matching the matrix and
 * the engine's per-instance selector).
 */
function filterMissionForView(
  missionSubkpis: any[],
  classification: Record<string, InstanceLatenessState>,
  showAll: boolean,
): any[] {
  if (showAll) return missionSubkpis;
  return missionSubkpis.filter((sk: any) => {
    const id = String(
      sk?.outcome_id || sk?.subKpiKey || sk?.instance_guid || sk?.label || '',
    ).toUpperCase();
    return isActionableState(classification[id]);
  });
}

// ── Types ───────────────────────────────────────────────────

export interface DataTabProps {
  /** All visible project rows from App.tsx, with Action Center attachments. */
  data: any[];
  /** Bumps projectConfigVersion in App so post-save re-fetch triggers. */
  onProjectConfigChanged?: () => void;
}

// ── Component ───────────────────────────────────────────────

const DataTab: React.FC<DataTabProps> = ({ data, onProjectConfigChanged }) => {
  const { t } = useAppTranslation('pages');
  const [showAll, setShowAll] = useState(false);
  const asOfDate = todayIso();

  /*
    Per-project collapse state. Persists across sessions so a user who
    works through the list yesterday and collapses handled projects
    doesn't see them re-expanded today. The set holds upper-cased
    project GUIDs of currently-collapsed projects (default = expanded).
  */
  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(
    loadCollapsedProjects,
  );

  const toggleProjectCollapsed = useCallback((projectGuid: string) => {
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectGuid)) next.delete(projectGuid);
      else next.add(projectGuid);
      saveCollapsedProjects(next);
      return next;
    });
  }, []);

  // Build per-project view models. Filter rows down to ones with at least
  // one actionable instance (when in default mode) AND user can edit.
  const projectViews = useMemo(() => {
    const out: Array<{
      projectGuid: string;
      projectName: string;
      classification: Record<string, InstanceLatenessState>;
      counts: { due: number; overdue: number };
      scheduleInstances: SubKpiInstance[];
      financeInstances: SubKpiInstance[];
      missionSubkpis: any[];
      latestSchedule: Record<string, any>;
      latestFinance: Record<string, any>;
      missionTargets: Record<string, any>;
      canEdit: boolean;
    }> = [];

    for (const row of data || []) {
      const projectGuid = String(
        row?.project_guid || row?.project_GUID || '',
      ).toUpperCase();
      if (!projectGuid) continue;

      const canEdit = !!row?.action_center_can_edit_kpi;
      if (!canEdit) continue; // can't act on this project

      /*
        Only non-terminal projects generate data entry obligations:
        Proposed, Planned, and In Progress all qualify. Pre-mobilization
        steps (preflight items, partner readiness, baseline data) happen
        before "In Progress" status, so filtering those out would hide
        legitimate work. Only Abandoned and Completed are dropped — they
        are terminal states with no further reporting expected.

        The user can still backfill historical entries on any project
        from its Performance tab directly. This filter only governs the
        Action Center surfacing.
      */
      if (!row?._isActiveProject) continue;

      const expected: ExpectedInstanceInput[] =
        row?.data_reliability_expected_instances || [];
      const classification = classifyAllForRow(expected, asOfDate);

      let due = 0;
      let overdue = 0;
      for (const state of Object.values(classification)) {
        if (state === 'due') due += 1;
        else if (state === 'overdue') overdue += 1;
      }

      // In actionable-only mode (default), drop projects with zero
      // actionable items so the surface stays focused. In show-all mode,
      // we still drop projects with zero KPI configuration entirely
      // (nothing to enter).
      const totalActionable = due + overdue;
      const project = row?.project || row || {};
      const scheduleInstances: SubKpiInstance[] =
        row?.action_center_schedule_instances || [];
      const financeInstances: SubKpiInstance[] =
        row?.action_center_finance_instances || [];
      /*
        DEV-822: HSE sub-KPIs are stored under the mission category but
        are event-driven (incidents → log entry, not a cadenced report).
        Filter them out here so they never reach MissionDataEntry. Even
        in show-all mode we don't want HSE rows pressuring users to
        record values for "this month's incidents" — they'd either skip
        or fabricate. HSE is reported via the project's incidents log.
      */
      const missionSubkpis: any[] = excludeHse(row?.mission_subkpis || []);

      const hasAnyConfig =
        scheduleInstances.length > 0 ||
        financeInstances.length > 0 ||
        missionSubkpis.length > 0;

      if (!showAll && totalActionable === 0) continue;
      if (showAll && !hasAnyConfig) continue;

      out.push({
        projectGuid,
        projectName: String(project?.project_name || '(unnamed)'),
        classification,
        counts: { due, overdue },
        scheduleInstances,
        financeInstances,
        missionSubkpis,
        latestSchedule: row?.action_center_latest_schedule_points || {},
        latestFinance: row?.action_center_latest_finance_points || {},
        missionTargets: row?.mission_targets || {},
        canEdit,
      });
    }

    // Sort: most overdue first, then most due, then name.
    out.sort((a, b) => {
      if (a.counts.overdue !== b.counts.overdue) {
        return b.counts.overdue - a.counts.overdue;
      }
      if (a.counts.due !== b.counts.due) {
        return b.counts.due - a.counts.due;
      }
      return a.projectName.localeCompare(b.projectName);
    });

    return out;
  }, [data, asOfDate, showAll]);

  return (
    <div className={styles.root}>
      {/* Actionable-only ↔ Show all toggle */}
      <div className={styles.viewControls}>
        <label className={styles.toggleLabel}>
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
          />
          <span>{t('actionCenter.data.showAll')}</span>
        </label>
        <span className={styles.viewHint}>
          {showAll
            ? t('actionCenter.data.showAllHint')
            : t('actionCenter.data.actionableOnlyHint')}
        </span>
      </div>

      {projectViews.length === 0 ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyTitle}>
            {showAll
              ? t('actionCenter.data.emptyAllTitle')
              : t('actionCenter.data.emptyActionableTitle')}
          </span>
          <span className={styles.emptyHint}>
            {showAll
              ? t('actionCenter.data.emptyAllHint')
              : t('actionCenter.data.emptyActionableHint')}
          </span>
        </div>
      ) : (
        <div className={styles.projectList}>
          {projectViews.map((p) => {
            const visibleSchedule = filterInstancesForView(
              p.scheduleInstances,
              p.classification,
              showAll,
            );
            const visibleFinance = filterInstancesForView(
              p.financeInstances,
              p.classification,
              showAll,
            );
            const visibleMission = filterMissionForView(
              p.missionSubkpis,
              p.classification,
              showAll,
            );

            const hasAnyVisible =
              visibleSchedule.length > 0 ||
              visibleFinance.length > 0 ||
              visibleMission.length > 0;
            if (!hasAnyVisible) return null;

            const isCollapsed = collapsedProjects.has(p.projectGuid);
            const bodyId = `actc-body-${p.projectGuid}`;

            return (
              <article
                key={p.projectGuid}
                className={styles.projectCard}
                aria-labelledby={`actc-${p.projectGuid}`}
              >
                <header
                  className={styles.projectHeader}
                  onClick={() => toggleProjectCollapsed(p.projectGuid)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleProjectCollapsed(p.projectGuid);
                    }
                  }}
                  aria-expanded={!isCollapsed}
                  aria-controls={bodyId}
                >
                  <button
                    type="button"
                    className={`${styles.collapseBtn} ${isCollapsed ? styles.collapseBtnCollapsed : ''}`}
                    onClick={(e) => {
                      // Stop the header's onClick from firing twice when the
                      // user clicks the chevron specifically. Header click
                      // still toggles for click-anywhere-on-header UX.
                      e.stopPropagation();
                      toggleProjectCollapsed(p.projectGuid);
                    }}
                    aria-label={
                      isCollapsed
                        ? t('actionCenter.data.expandProject')
                        : t('actionCenter.data.collapseProject')
                    }
                  >
                    {/* Chevron — rotates 90° via CSS when collapsed */}
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <h2 id={`actc-${p.projectGuid}`} className={styles.projectName}>
                    {p.projectName}
                  </h2>
                  <div className={styles.projectMeta}>
                    {p.counts.overdue > 0 && (
                      <span className={`${styles.metaPill} ${styles.metaOverdue}`}>
                        {t('actionCenter.data.overdueCount', {
                          count: p.counts.overdue,
                        })}
                      </span>
                    )}
                    {p.counts.due > 0 && (
                      <span className={`${styles.metaPill} ${styles.metaDue}`}>
                        {t('actionCenter.data.dueCount', { count: p.counts.due })}
                      </span>
                    )}
                  </div>
                </header>

                {!isCollapsed && (
                  <div id={bodyId} className={styles.projectBody}>
                    {visibleSchedule.length > 0 && (
                      <section className={styles.areaSection}>
                        <h3 className={styles.areaTitle}>
                          {t('actionCenter.data.areas.schedule')}
                        </h3>
                        <ScheduleDataEntry
                          projectGuid={p.projectGuid}
                          scheduleInstances={visibleSchedule}
                          latestPointsByInstance={p.latestSchedule}
                          canEdit={p.canEdit}
                          onDataSaved={onProjectConfigChanged}
                        />
                      </section>
                    )}

                    {visibleFinance.length > 0 && (
                      <section className={styles.areaSection}>
                        <h3 className={styles.areaTitle}>
                          {t('actionCenter.data.areas.finance')}
                        </h3>
                        <BudgetDataEntry
                          projectGuid={p.projectGuid}
                          financeInstances={visibleFinance}
                          latestPointsByInstance={p.latestFinance}
                          canEdit={p.canEdit}
                          onDataSaved={onProjectConfigChanged}
                        />
                      </section>
                    )}

                    {visibleMission.length > 0 && (
                      <section className={styles.areaSection}>
                        <h3 className={styles.areaTitle}>
                          {t('actionCenter.data.areas.mission')}
                        </h3>
                        <MissionDataEntry
                          projectGuid={p.projectGuid}
                          missionSubkpis={visibleMission}
                          projectTargets={p.missionTargets}
                          canEdit={p.canEdit}
                          onDataSaved={onProjectConfigChanged}
                        />
                      </section>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DataTab;
